/**
 * locale-detect — ichina.co contract, mapped onto jubuddy Locale ids.
 *
 * Language ≠ geography. Browser language wins; IP is only used to
 * (a) disambiguate a bare `zh` tag into zh vs zh-TW, or (b) pick a
 * locale when the browser matches nothing we ship. An English
 * browser in Hong Kong stays English.
 *
 * Site-native codes stay `en | zh | zh-TW | ja | ko | th | nl | es | fr`.
 * Aliases (zh-Hans, zh-CN, zh-Hant, zh-HK, …) are accepted on READ and
 * written back as `zh` / `zh-TW`. Bare browser `zh` is AMBIGUOUS —
 * it is not collapsed to Simplified.
 *
 * Country lookup is shared with geo-city (one cached promise).
 */
import { SUPPORTED_LOCALES, isLocale, type Locale } from './types.ts';

const IP_TIMEOUT_MS = 2000;

/**
 * First path segment of /planet, /hk, and every city globe.
 * Keep this list next to WORLDS in planet/data/worlds.ts — locale-detect.test
 * iterates WORLDS so a new city that is missing here fails CI. Do NOT import
 * WORLDS here: that file pulls districts.ts (~500KB) into every i18n page.
 */
const PLANET_PATH_HEADS: ReadonlySet<string> = new Set([
  'planet',
  'hk',
  'tokyo',
  'nyc',
  'paris',
  'amsterdam',
  'rotterdam',
  'berlin',
  'sanfrancisco',
  'vienna',
  'barcelona',
  'copenhagen',
  'toronto',
  'melbourne',
  'edinburgh',
  'seoul',
  'losangeles',
  'dubai',
  'bangkok',
  'capetown',
  'rio',
  'istanbul',
  'venice',
  'shenzhen',
  'taipei',
  'boston',
  'mumbai',
  'moscow',
  'nanjing',
  'beijing',
  'shanghai',
  'sydney',
  'kualalumpur',
  'washingtondc',
  'xian',
  'singapore',
  'rome',
  'london',
  'jakarta',
  'mexicocity',
  'sanjose',
  'telaviv',
  'chicago',
  'sport',
  'games',
  'geneva',
  'macau',
  'monaco',
  'vatican',
  'kyoto',
  'prague',
  'zurich',
  'helsinki',
  'lisbon',
  'athens',
  'osaka',
  'yokohama',
  'nagoya',
  'fukuoka',
  'zhengzhou',
  'luoyang',
  'miami',
]);

/** Portal codes dseek PORTAL_LANGS accepts. Not an i18n Locale (`tc` ≠ zh-TW). */
export type PortalLang = 'en' | 'tc' | 'ko' | 'ja' | 'th';

/** HUD locale → model ask-lang. Simplified stays `zh` (not a portal code). */
const ASK_LANG: Record<Locale, string> = {
  en: 'en',
  'zh-TW': 'tc',
  zh: 'zh',
  ja: 'ja',
  ko: 'ko',
  th: 'th',
  nl: 'nl',
  es: 'es',
  fr: 'fr',
};

/** HUD locale → dseek `?lang=`. Locales the portal does not speak are null. */
const PORTAL_LANG: Record<Locale, PortalLang | null> = {
  en: 'en',
  'zh-TW': 'tc',
  ja: 'ja',
  ko: 'ko',
  th: 'th',
  zh: null,
  nl: null,
  es: null,
  fr: null,
};

/** Language the pin/chat model should answer in. Follows live HUD locale. */
export function localeToAskLang(locale: Locale): string {
  return ASK_LANG[locale];
}

/**
 * Language written onto dseek/iso/lens `?lang=`.
 * `zh` / `nl` / `es` / `fr` are null — never send Simplified as `tc`.
 */
export function localeToPortalLang(locale: Locale): PortalLang | null {
  return PORTAL_LANG[locale];
}

/**
 * portalBridge.ts PORTAL_LANGS = en|tc|ko|ja|th. `tc` is Traditional
 * Chinese on the flat-map handoff and is NOT an i18n Locale. We map
 * it only when READING ?lang= on a planet route so the cookie/storage
 * preference matches the portal, without rewriting the URL.
 */
const PORTAL_TC = 'tc';

const countryLocale: Record<string, Locale> = {
  CN: 'zh',
  TW: 'zh-TW',
  HK: 'zh-TW',
  MO: 'zh-TW',
  JP: 'ja',
  KR: 'ko',
  TH: 'th',
  NL: 'nl',
  BE: 'nl',
};

export function isPlanetLocalePath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const head = pathname.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
  return PLANET_PATH_HEADS.has(head);
}

/**
 * True when setLocale / a winning marketing ?lang= may write `?lang=`
 * via history.replaceState. Planet/city routes keep portalBridge's
 * ?lang= (including `tc`) untouched — cookie + localStorage only.
 */
export function shouldPersistLocaleQuery(
  pathname: string | null | undefined,
  search?: string | null,
): boolean {
  if (isPlanetLocalePath(pathname)) return false;
  if (!search) return true;
  try {
    const current = new URLSearchParams(search.startsWith('?') ? search : `?${search}`).get('lang');
    return current !== PORTAL_TC;
  } catch {
    return true;
  }
}

export function localeFromCountry(country: string | null | undefined): Locale | null {
  if (!country) return null;
  return countryLocale[country.toUpperCase()] ?? null;
}

/**
 * Map a raw tag (URL / cookie / storage / alias) onto a site-native
 * Locale. Bare `zh` is a first-class site id here (explicit preference).
 * Browser-tag ambiguity is handled by `mapBrowserTag`, not this.
 */
export function canonicalLocale(raw: string | null | undefined): Locale | null {
  if (!raw) return null;
  const normalized = raw.trim().replace(/_/g, '-');
  if (!normalized) return null;
  if (isLocale(normalized)) return normalized;
  const lower = normalized.toLowerCase();
  if (lower === 'zh-tw') return 'zh-TW';
  for (const loc of SUPPORTED_LOCALES) {
    if (loc.toLowerCase() === lower) return loc;
  }
  const mapped = mapBrowserTag(normalized);
  if (mapped && mapped !== 'zh-ambiguous' && isLocale(mapped)) return mapped;
  return null;
}

function mapBrowserTag(tag: string): Locale | 'zh-ambiguous' | null {
  const raw = tag.trim().replace(/_/g, '-');
  if (!raw) return null;
  const parts = raw.split('-');
  const lang = (parts[0] || '').toLowerCase();
  const region = (parts[1] || '').toLowerCase();

  if (lang === 'zh') {
    if (region === 'hans' || region === 'cn' || region === 'sg') return 'zh';
    if (region === 'hant' || region === 'tw' || region === 'hk' || region === 'mo') return 'zh-TW';
    return 'zh-ambiguous';
  }
  if (lang === 'ja') return 'ja';
  if (lang === 'ko') return 'ko';
  if (lang === 'th') return 'th';
  if (lang === 'nl') return 'nl';
  if (lang === 'es') return 'es';
  if (lang === 'fr') return 'fr';
  if (lang === 'en') return 'en';
  return null;
}

export function localesFromBrowser(
  tags?: readonly string[] | null,
): { match: Locale | null; zhAmbiguous: boolean } {
  const list: readonly string[] = tags
    ? tags
    : typeof navigator === 'undefined'
      ? []
      : navigator.languages?.length
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : [];
  let zhAmbiguous = false;
  for (const tag of list) {
    const mapped = mapBrowserTag(tag);
    if (mapped === 'zh-ambiguous') {
      zhAmbiguous = true;
      continue;
    }
    if (mapped && isLocale(mapped)) return { match: mapped, zhAmbiguous };
  }
  return { match: null, zhAmbiguous };
}

async function fetchText(url: string, ms: number): Promise<string | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

let countryPromise: Promise<string | null> | null = null;

async function lookupCountryFromIpOnce(): Promise<string | null> {
  const trace = await fetchText('https://www.cloudflare.com/cdn-cgi/trace', IP_TIMEOUT_MS);
  if (trace) {
    const loc = trace.match(/(?:^|\n)loc=([A-Z]{2})(?:\n|$)/);
    if (loc && loc[1] !== 'XX' && loc[1] !== 'T1') return loc[1];
  }

  const who = await fetchText('https://ipwho.is/', IP_TIMEOUT_MS);
  if (who) {
    try {
      const json = JSON.parse(who) as { success?: boolean; country_code?: string };
      const code = json.country_code?.toUpperCase();
      if (code && /^[A-Z]{2}$/.test(code)) return code;
    } catch {
      /* ignore malformed geo payload */
    }
  }
  return null;
}

/** Shared country promise — language-detect and city-detect hit the network once. */
export function lookupCountryFromIp(): Promise<string | null> {
  countryPromise ??= lookupCountryFromIpOnce();
  return countryPromise;
}

/** Test hook — clears the cached country promise between cases. */
export function resetCountryLookupForTests(): void {
  countryPromise = null;
}

/**
 * Async resolver used AFTER first paint when url/cookie/storage did
 * not already choose. IP never overrides an explicit non-zh browser
 * match (EN in HK stays EN).
 */
export async function detectPreferredLocale(
  browserLanguages?: readonly string[] | null,
): Promise<Locale> {
  const { match, zhAmbiguous } = localesFromBrowser(browserLanguages);

  if (match) return match;

  if (zhAmbiguous) {
    const fromIp = localeFromCountry(await lookupCountryFromIp());
    if (fromIp === 'zh' || fromIp === 'zh-TW') return fromIp;
    return 'en';
  }

  return localeFromCountry(await lookupCountryFromIp()) ?? 'en';
}

/**
 * Read `?lang=` as an i18n locale.
 *
 * Marketing URLs accept aliases (zh-Hans → zh). Planet/city routes
 * accept exact site codes plus the portal-only `tc` → zh-TW map;
 * they do NOT accept zh-Hans/zh-CN aliases so we never confuse the
 * two `?lang=` contracts.
 */
export function localeFromQueryParam(
  raw: string | null | undefined,
  pathname?: string | null,
): Locale | null {
  if (!raw) return null;
  if (isPlanetLocalePath(pathname)) {
    if (raw === PORTAL_TC) return 'zh-TW';
    return isLocale(raw) ? raw : null;
  }
  return canonicalLocale(raw);
}
